import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, apiFetch, getEntityTypesList } from "../../services/api";
import EntityReadOnlyView from "../../components/EntityReadOnlyView";
import EntityForm from "../../components/EntityForm";
import GraphVisualization from "../../components/GraphVisualization";
import GenerateDocumentButton from "../../components/GenerateDocumentButton";
import EnrichmentEntityPickerModal from "../../components/EnrichmentEntityPickerModal";
import { v2cx } from "../theme/tokens";
import { useV2Theme } from "../theme/ThemeProvider";
import { V2_ROUTES } from "../state/useV2Navigation";
import {
  markKeepKnowledgePrefill,
  stashKnowledgePrefill,
} from "../../utils/knowledgePrefill";
import { userCanContribute } from "../../utils/userRoles";
import SubmitForBaselineApprovalModal from "../components/SubmitForBaselineApprovalModal";
import V2OverflowMenu, {
  V2OverflowMenuItem,
  V2OverflowMenuSeparator,
} from "../components/V2OverflowMenu";
import { formatEntityMapStatusLabel } from "../../utils/enrichmentJobCard";
import { isApplicationEntity } from "../../components/relationships/SectionConfig";
import {
  filterExplorerBrowseEntities,
  userIsCatalogSteward,
  userMaySeeProposedCatalog,
} from "../../utils/explorerVisibility";
import { useOwnProposedEntityCount } from "../../utils/ownProposedInventory";
import {
  canShowExplorerMergeAction,
  getExplorerMergeEntityType,
} from "./explorerMergeUtils";
import { useUnsavedEntityDetailLeaveGuard } from "../../hooks/useUnsavedEntityDetailLeaveGuard";
import UnsavedEntityDetailChangesDialog from "../../components/UnsavedEntityDetailChangesDialog";
import { EMPTY_UNSAVED_ENTITY_DETAIL_SUMMARY } from "../../utils/entityDetailLeaveGuard";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import { useMobileChrome } from "../mobile/MobileChromeContext";
import MobileBottomSheet from "../components/mobile/MobileBottomSheet";
import {
  scrollNearestScrollportToEl,
  scrollShellMainToTop,
} from "../utils/scrollShell";

const TAB_KEYS = {
  browse: "browse",
  detail: "detail",
  create: "create",
};

/** Proposed catalog item that can still be submitted for baseline approval. */
const isEntityPendingForApproval = (entity) => {
  if (!entity?.id) return false;
  const approval = String(
    entity?.approval_status || entity?.approvalStatus || ""
  )
    .trim()
    .toLowerCase();
  return approval === "proposed" && !entity?.awaiting_baseline_approval;
};

const splitAliasNames = (value) =>
  String(value || "")
    .split(/[,;|]+/)
    .map((part) => part.trim())
    .filter(Boolean);

const aliasesMatchingSearch = (aliasNames, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return [];
  return splitAliasNames(aliasNames).filter((alias) =>
    alias.toLowerCase().includes(q)
  );
};

const V2ExplorerPage = ({
  initialEntityTypeFilter = "all",
  onBackToGallery = null,
  onNavigate = null,
  initialOpenCreate = false,
  initialEntityId = null,
  initialFocusSearch = false,
  initialProposedOnly = false,
  initialSelectionMode = false,
  skipOwnProposedCount = false,
}) => {
  const { tokens } = useV2Theme();
  const { isMobile } = useDeviceDetection();
  const { setChrome } = useMobileChrome();
  const canContribute = userCanContribute();
  const detailAnchorRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectedEntityRef = useRef(null);
  const loadSeqRef = useRef(0);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    if (initialOpenCreate) return TAB_KEYS.create;
    if (String(initialEntityId || "").trim()) return TAB_KEYS.detail;
    return TAB_KEYS.browse;
  });
  const [entityTypeOptions, setEntityTypeOptions] = useState([]);
  const [entityTypeFilter, setEntityTypeFilter] = useState(initialEntityTypeFilter || "all");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [approvalFilter, setApprovalFilter] = useState(
    initialProposedOnly ? "proposed" : "all"
  );
  const [entities, setEntities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selectedEntity, setSelectedEntity] = useState(() => {
    const id = String(initialEntityId || "").trim();
    return id ? { id } : null;
  });
  const [selectedEntityFull, setSelectedEntityFull] = useState(null);
  const [loadingSelected, setLoadingSelected] = useState(() =>
    Boolean(String(initialEntityId || "").trim())
  );
  const [showGraphPanel, setShowGraphPanel] = useState(false);
  const {
    summary: unsavedEntityDetailSummary,
    setSummary: setUnsavedEntityDetailSummary,
    dialogOpen: unsavedEntityDetailDialogOpen,
    requestLeave: requestLeaveEntityDetail,
    stayOnPage: stayOnEntityDetailPage,
    leaveWithoutSaving: leaveEntityDetailWithoutSaving,
  } = useUnsavedEntityDetailLeaveGuard();
  const isCreateTab = activeTab === TAB_KEYS.create;
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    entityId: null,
    entityName: "",
  });
  const [deletingEntity, setDeletingEntity] = useState(false);
  const [deleteNotice, setDeleteNotice] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deletingEntityLabel, setDeletingEntityLabel] = useState("");
  const [mergePickerState, setMergePickerState] = useState({
    open: false,
    source: null,
  });
  const [mergeConfirm, setMergeConfirm] = useState({
    open: false,
    source: null,
    target: null,
  });
  const [mergingEntity, setMergingEntity] = useState(false);
  const [mergeNotice, setMergeNotice] = useState("");
  const [mergeError, setMergeError] = useState("");
  const canSeeOwnProposed = useMemo(() => userMaySeeProposedCatalog(), []);
  const isSteward = useMemo(() => userIsCatalogSteward(), []);
  const { hasOwnProposed, refresh: refreshOwnProposedCount } = useOwnProposedEntityCount({
    enabled: !skipOwnProposedCount,
  });
  const [selectionMode, setSelectionMode] = useState(Boolean(initialSelectionMode));
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [submitNotice, setSubmitNotice] = useState("");
  const [createFormKey, setCreateFormKey] = useState(0);
  const [entityNavStack, setEntityNavStack] = useState([]);

  useEffect(() => {
    selectedEntityRef.current = selectedEntity;
  }, [selectedEntity]);

  const proposedOnPage = useMemo(
    () =>
      entities.filter((entity) => {
        const approval = String(
          entity?.approval_status || entity?.approvalStatus || ""
        )
          .trim()
          .toLowerCase();
        if (approval !== "proposed") return false;
        if (selectionMode && entity?.awaiting_baseline_approval) return false;
        return true;
      }),
    [entities, selectionMode]
  );

  useEffect(() => {
    if (skipOwnProposedCount) return;
    if (!hasOwnProposed && proposedOnPage.length === 0) {
      setSelectionMode(false);
      setSelectedIds(new Set());
    }
  }, [hasOwnProposed, skipOwnProposedCount, proposedOnPage.length]);

  const selectAllPendingOnPage = useCallback(() => {
    setSelectedIds(new Set(proposedOnPage.map((entity) => String(entity.id))));
  }, [proposedOnPage]);

  const loadEntityTypes = useCallback(async () => {
    try {
      const raw = await getEntityTypesList(true);
      setEntityTypeOptions(
        (Array.isArray(raw) ? raw : []).filter(
          (item) => item?.value
        )
      );
    } catch {
      setEntityTypeOptions([]);
    }
  }, []);

  const refreshEntities = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setRefreshing(true);
    setError("");

    // Orphan-interface / SC filters apply on the mixed "All" browse only.
    // Typed tiles (Applications, Connections, …) share the same approval
    // visibility: Approved + the viewer's own Proposed.
    const needsExplorerFilter = entityTypeFilter === "all";
    const qs = new URLSearchParams();
    qs.set("page", String(page));
    qs.set("page_size", String(pageSize));
    if (needsExplorerFilter) {
      qs.set("architecture_explorer", "true");
    }
    if (approvalFilter === "proposed") {
      qs.set("include_unapproved", "true");
      qs.set("approval_status", "Proposed");
    }
    if (selectionMode) {
      qs.set("submittable_only", "true");
    }
    if (searchTerm.trim()) {
      qs.set("search", searchTerm.trim());
    }
    if (entityTypeFilter && entityTypeFilter !== "all") {
      qs.set("entity_type", entityTypeFilter);
    }

    try {
      const response = await apiFetch(`/api/entities-paginated?${qs}`, {
        cache: "no-store",
        timeout: 120000,
      });
      if (seq !== loadSeqRef.current) return;
      if (!response?.ok) {
        throw new Error(`Failed to load items (${response?.status || "unknown"})`);
      }
      const json = await response.json();
      const payload = json?.data || json;
      setEntities(filterExplorerBrowseEntities(payload?.entities || []));
      setTotal(
        Number(payload?.pagination?.total_items ?? json?.total ?? payload?.entities?.length ?? 0)
      );
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      setError(err?.message || "Failed to load items.");
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [approvalFilter, entityTypeFilter, page, pageSize, searchTerm, selectionMode]);

  useEffect(() => {
    void refreshEntities();
  }, [refreshEntities]);

  const toggleSelected = useCallback((entityId) => {
    const id = String(entityId || "").trim();
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectionMode(false);
  }, []);

  const loadSelectedEntityFull = useCallback(async (entity, options = {}) => {
    const { bypassCache = false } = options;
    const id = String(entity?.id || "").trim();
    if (!id) {
      setSelectedEntityFull(null);
      return;
    }
    setLoadingSelected(true);
    try {
      const full = await api.getEntity(id, {
        includeRelationships: true,
        includeRelatedEntities: true,
        bypassCache,
        includeUnapproved: true,
      });
      const fullType = full?.type || full?.entity_type;
      const sameTypeId =
        String(full?.entity_type_id || "") ===
        String(entity?.entity_type_id || "");
      const preserveListType =
        Boolean(entity?.type) &&
        fullType === "application" &&
        entity.type !== "application" &&
        (sameTypeId || !full?.entity_type_id);
      const resolvedType = preserveListType
        ? entity.type
        : fullType || entity?.type;
      const mergedFull = full ? { ...full, type: resolvedType } : null;
      if (full?.id && String(full.id) !== id) {
        setSelectedEntity((prev) => ({
          ...(prev || {}),
          ...mergedFull,
          id: mergedFull.id,
          name: mergedFull.name,
          type: mergedFull.type || prev?.type,
        }));
      }
      setSelectedEntityFull(mergedFull || null);
    } catch {
      setSelectedEntityFull(entity || null);
    } finally {
      setLoadingSelected(false);
    }
  }, []);

  useEffect(() => {
    void loadEntityTypes();
  }, [loadEntityTypes]);

  useEffect(() => {
    setSelectedEntity((prev) => {
      if (!prev?.id || !entities.length) return prev;
      const matched = entities.find((e) => String(e?.id) === String(prev.id));
      return matched || prev;
    });
  }, [entities]);

  useEffect(() => {
    const id = String(initialEntityId || "").trim();
    if (!id) return undefined;
    let cancelled = false;
    setActiveTab(TAB_KEYS.detail);
    setSelectedEntity((prev) =>
      prev && String(prev.id) === id ? prev : { id }
    );
    setLoadingSelected(true);
    void (async () => {
      try {
        const full = await api.getEntity(id, {
          includeRelationships: true,
          includeRelatedEntities: true,
          bypassCache: true,
          includeUnapproved: true,
        });
        if (cancelled || !full?.id) return;
        setSelectedEntity(full);
        setActiveTab(TAB_KEYS.detail);
        await loadSelectedEntityFull(full, { bypassCache: true });
      } catch {
        if (!cancelled) {
          setError("Could not open that entity. It may have been deleted.");
          setSelectedEntity(null);
          setActiveTab(TAB_KEYS.browse);
        }
      } finally {
        if (!cancelled) setLoadingSelected(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialEntityId, loadSelectedEntityFull]);

  const submitSearch = useCallback(() => {
    setSearchTerm(searchInput.trim());
    setPage(1);
  }, [searchInput]);

  const handleSearchInputChange = useCallback(
    (nextValue) => {
      setSearchInput(nextValue);
      if (!String(nextValue || "").trim() && searchTerm) {
        setSearchTerm("");
        setPage(1);
      }
    },
    [searchTerm]
  );

  useEffect(() => {
    if (!initialFocusSearch) return undefined;
    const timer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [initialFocusSearch]);

  useEffect(() => {
    if (!selectedEntity?.id) {
      setSelectedEntityFull(null);
      return;
    }
    void loadSelectedEntityFull(selectedEntity);
  }, [selectedEntity, loadSelectedEntityFull]);

  useEffect(() => {
    setShowGraphPanel(false);
  }, [selectedEntity?.id, activeTab]);

  // Graph is desktop-only — never leave the panel open on mobile.
  useEffect(() => {
    if (isMobile) setShowGraphPanel(false);
  }, [isMobile]);

  useEffect(() => {
    setUnsavedEntityDetailSummary(EMPTY_UNSAVED_ENTITY_DETAIL_SUMMARY);
  }, [selectedEntity?.id, setUnsavedEntityDetailSummary]);

  useEffect(() => {
    if (activeTab !== TAB_KEYS.detail) {
      setUnsavedEntityDetailSummary(EMPTY_UNSAVED_ENTITY_DETAIL_SUMMARY);
    }
  }, [activeTab, setUnsavedEntityDetailSummary]);

  // Scroll once when opening an embedded Map drill-in. Intentionally mount-only:
  // depending on onBackToGallery re-fires when parents pass a new inline lambda
  // and yanks scroll back to the top on every re-render.
  const embeddedInGallery = Boolean(onBackToGallery);
  useLayoutEffect(() => {
    if (!embeddedInGallery) return undefined;
    scrollShellMainToTop();
    return undefined;
  }, [embeddedInGallery]);

  useLayoutEffect(() => {
    if (activeTab !== TAB_KEYS.detail || !selectedEntity?.id) return;
    // Avoid scrollIntoView — it pans the shell and can hide header/tabs.
    scrollNearestScrollportToEl(detailAnchorRef.current, { offset: 8 });
  }, [activeTab, selectedEntity?.id]);

  const totalPages = useMemo(() => {
    if (!total || !pageSize) return 1;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [pageSize, total]);

  const allEntitiesMap = useMemo(() => {
    const map = {};
    const addEntity = (entity) => {
      const id = String(entity?.id || "").trim();
      if (id) map[id] = entity;
    };
    entities.forEach(addEntity);
    const relEntities = selectedEntityFull?.related_entities;
    if (Array.isArray(relEntities)) relEntities.forEach(addEntity);
    else if (relEntities && typeof relEntities === "object") {
      Object.values(relEntities).forEach((value) => {
        if (Array.isArray(value)) value.forEach(addEntity);
      });
    }
    if (selectedEntityFull) addEntity(selectedEntityFull);
    return map;
  }, [entities, selectedEntityFull]);

  const clearEntityNavStack = useCallback(() => {
    setEntityNavStack([]);
  }, []);

  const runIfAllowedToLeaveEntityDetail = useCallback(
    (action) => {
      requestLeaveEntityDetail(action);
    },
    [requestLeaveEntityDetail]
  );

  const handleNavigateToRelatedEntity = useCallback(
    (relatedRef) => {
      const relatedId = String(relatedRef?.id || "").trim();
      if (!relatedId) return;

      const current = selectedEntityFull || selectedEntity;
      const currentId = String(current?.id || "").trim();
      if (!currentId || currentId === relatedId) return;

      setEntityNavStack((prev) => [
        ...prev,
        {
          id: currentId,
          name: current?.name || "Previous item",
          entity: selectedEntity,
          entityFull: selectedEntityFull,
        },
      ]);

      const fromMap = allEntitiesMap[relatedId];
      const nextEntity =
        fromMap ||
        ({
          id: relatedId,
          name: relatedRef?.name || "Unnamed item",
          type: relatedRef?.type || relatedRef?.entity_type || null,
          entity_type: relatedRef?.entity_type || relatedRef?.type || null,
        });

      setSelectedEntity(nextEntity);
      setSelectedEntityFull(null);
      setActiveTab(TAB_KEYS.detail);
      setShowGraphPanel(false);
      void loadSelectedEntityFull(nextEntity);
    },
    [
      allEntitiesMap,
      loadSelectedEntityFull,
      selectedEntity,
      selectedEntityFull,
    ]
  );

  const handleNavigateBackFromRelated = useCallback(() => {
    setEntityNavStack((prev) => {
      if (!prev.length) return prev;
      const nextStack = [...prev];
      const previous = nextStack.pop();
      if (previous) {
        const restored =
          previous.entity || { id: previous.id, name: previous.name };
        setSelectedEntity(restored);
        setSelectedEntityFull(previous.entityFull || null);
        setActiveTab(TAB_KEYS.detail);
        setShowGraphPanel(false);
        if (!previous.entityFull) {
          void loadSelectedEntityFull(restored);
        }
      }
      return nextStack;
    });
  }, [loadSelectedEntityFull]);

  const entityNavBack = useMemo(() => {
    if (!entityNavStack.length) return null;
    const previous = entityNavStack[entityNavStack.length - 1];
    const label = previous?.name
      ? `Back to ${previous.name}`
      : "Back to previous item";
    return { label, onBack: handleNavigateBackFromRelated };
  }, [entityNavStack, handleNavigateBackFromRelated]);

  const openAddKnowledgeForEntity = useCallback(
    (entity) => {
      if (!entity?.id) return;
      stashKnowledgePrefill({
        scopeType: "entity",
        id: entity.id,
        name: entity.name || entity.entity_name || "",
        extractMode: "auto",
      });
      markKeepKnowledgePrefill();
      try {
        sessionStorage.setItem("dp_open_add_from_map", "1");
      } catch {
        /* ignore */
      }
      if (typeof onBackToGallery === "function") {
        onBackToGallery();
        window.setTimeout(() => {
          window.dispatchEvent(new CustomEvent("dp-open-map-add"));
        }, 0);
      } else if (typeof onNavigate === "function") {
        onNavigate(V2_ROUTES.architectureGallery);
      }
    },
    [onBackToGallery, onNavigate]
  );

  /** Build graph nodes/edges from in-memory entity + relationships for instant paint */
  const explorerGraphBootstrap = useMemo(() => {
    const parseIdsLocal = (value) => {
      if (value == null) return [];
      if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
      if (typeof value === "string") {
        const t = value.trim();
        if (!t || t === "[]" || t === "null") return [];
        try {
          const p = JSON.parse(t);
          return Array.isArray(p) ? p.map((x) => String(x).trim()).filter(Boolean) : [];
        } catch {
          return t.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
        }
      }
      return [];
    };

    const ent = selectedEntityFull || selectedEntity;
    if (!ent?.id) return { initialNodes: [], initialEdges: [] };

    const related =
      ent.related_entities && typeof ent.related_entities === "object" && !Array.isArray(ent.related_entities)
        ? ent.related_entities
        : {};
    const rels = Array.isArray(ent.relationships) ? ent.relationships : [];

    const isValidGraphEntityId = (nid) => {
      const k = String(nid ?? "").trim();
      if (!k || k === "[]" || k === "null" || k === "undefined") return false;
      return true;
    };

    const nodesMap = new Map();
    const pushNode = (nid, extra = {}) => {
      const k = String(nid).trim();
      if (!k || !isValidGraphEntityId(k) || nodesMap.has(k)) return;
      const fromRel = related[k] || allEntitiesMap[k];
      nodesMap.set(k, {
        id: k,
        name: fromRel?.name ?? extra.name ?? `Entity ${k.slice(0, 8)}…`,
        type: fromRel?.type || fromRel?.entity_type || extra.type || "Unknown",
        entity_type: fromRel?.entity_type || fromRel?.type || extra.type,
        description: fromRel?.description ?? extra.description,
        parent: extra.parent,
        children: extra.children,
        predecessors: extra.predecessors,
        successors: extra.successors,
      });
    };

    pushNode(String(ent.id), {
      name: ent.name,
      type: ent.type || ent.entity_type,
      parent: ent.parent ? String(ent.parent) : null,
      children: parseIdsLocal(ent.children),
      predecessors: parseIdsLocal(ent.predecessors),
      successors: parseIdsLocal(ent.successors),
    });

    const edges = [];
    rels.forEach((r) => {
      const sid = r?.source_entity_id != null ? String(r.source_entity_id).trim() : "";
      const tid = r?.target_entity_id != null ? String(r.target_entity_id).trim() : "";
      if (!sid || !tid) return;
      pushNode(sid, {});
      pushNode(tid, {});
      edges.push({
        id: String(r.id ?? `${sid}-${tid}-${r.type || "rel"}`),
        source_entity_id: sid,
        target_entity_id: tid,
        source: sid,
        target: tid,
        type: r.type || "Unknown",
        data: {
          relationshipType: r.type || "Unknown",
          relationshipStatus: r.status,
        },
      });
    });

    parseIdsLocal(ent.children).forEach((cid) => pushNode(cid, {}));
    parseIdsLocal(ent.successors).forEach((cid) => pushNode(cid, {}));
    parseIdsLocal(ent.predecessors).forEach((cid) => pushNode(cid, {}));
    if (ent.parent && isValidGraphEntityId(ent.parent)) {
      pushNode(String(ent.parent).trim(), {});
    }

    return {
      initialNodes: Array.from(nodesMap.values()),
      initialEdges: edges,
    };
  }, [selectedEntityFull, selectedEntity, allEntitiesMap]);

  const createDefaultEntityType = useMemo(
    () => (entityTypeFilter !== "all" ? entityTypeFilter : "application"),
    [entityTypeFilter]
  );

  const handleEntitySaved = useCallback(
    async (savedEntity) => {
      if (savedEntity?.id) {
        setSelectedEntity(savedEntity);
        setActiveTab(TAB_KEYS.detail);
        await loadSelectedEntityFull(savedEntity, { bypassCache: true });
      } else {
        setActiveTab(TAB_KEYS.browse);
      }
      await refreshEntities();
    },
    [loadSelectedEntityFull, refreshEntities]
  );

  const handleRequestDeleteEntity = useCallback((entity) => {
    const entityId = String(entity?.id || "").trim();
    if (!entityId) return;
    setDeleteError("");
    setDeleteConfirm({
      open: true,
      entityId,
      entityName: String(entity?.name || "").trim() || "this item",
    });
  }, []);

  const cancelDeleteEntity = useCallback(() => {
    if (deletingEntity) return;
    setDeleteConfirm({ open: false, entityId: null, entityName: "" });
    setDeleteError("");
  }, [deletingEntity]);

  const dismissDeleteNotice = useCallback(() => {
    setDeleteNotice("");
  }, []);

  const dismissMergeNotice = useCallback(() => {
    setMergeNotice("");
  }, []);

  const confirmDeleteEntity = useCallback(async () => {
    const entityId = String(deleteConfirm.entityId || "").trim();
    const entityName = deleteConfirm.entityName || "this item";
    if (!entityId) return;
    setDeletingEntityLabel(entityName);
    setDeletingEntity(true);
    setDeleteNotice("");
    setDeleteError("");
    try {
      await api.deleteEntity(entityId);
      setDeleteConfirm({ open: false, entityId: null, entityName: "" });
      clearEntityNavStack();
      setSelectedEntity(null);
      setSelectedEntityFull(null);
      setShowGraphPanel(false);
      setActiveTab(TAB_KEYS.browse);
      setDeletingEntity(false);
      setDeletingEntityLabel("");
      setDeleteNotice(`“${entityName}” was removed from your map.`);
      window.setTimeout(() => setDeleteNotice(""), 6000);
      await refreshEntities();
    } catch (e) {
      setDeleteError(e?.message || "Failed to delete item.");
    } finally {
      setDeletingEntity(false);
      setDeletingEntityLabel("");
    }
  }, [deleteConfirm.entityId, deleteConfirm.entityName, refreshEntities, clearEntityNavStack]);

  const handleRequestMergeEntity = useCallback(
    (entity) => {
      if (!canShowExplorerMergeAction(entity, isSteward)) return;
      setMergeError("");
      setMergeNotice("");
      setMergeConfirm({ open: false, source: null, target: null });
      setMergePickerState({
        open: true,
        source: {
          id: String(entity?.id || "").trim(),
          name: String(entity?.name || "").trim() || "this item",
          type: String(entity?.type || entity?.entity_type || "").trim(),
          description: String(entity?.description || "").trim(),
          alias_names: entity?.alias_names || "",
        },
      });
    },
    [isSteward]
  );

  const closeMergePicker = useCallback(() => {
    if (mergingEntity) return;
    setMergePickerState({ open: false, source: null });
  }, [mergingEntity]);

  const closeMergeConfirm = useCallback(() => {
    if (mergingEntity) return;
    setMergeConfirm({ open: false, source: null, target: null });
    setMergeError("");
  }, [mergingEntity]);

  const handleSelectMergeTarget = useCallback((targetEntity) => {
    setMergePickerState((prev) => {
      if (!prev?.source) return prev;
      setMergeConfirm({
        open: true,
        source: prev.source,
        target: targetEntity,
      });
      return { open: false, source: null };
    });
    setMergeError("");
  }, []);

  const confirmMergeEntities = useCallback(async () => {
    const source = mergeConfirm.source;
    const target = mergeConfirm.target;
    const sourceId = String(source?.id || "").trim();
    const targetId = String(target?.id || "").trim();
    if (!sourceId || !targetId || sourceId === targetId) return;
    setMergingEntity(true);
    setMergeError("");
    setMergeNotice("");
    try {
      const result = await api.mergeEntities(sourceId, targetId);
      const targetPayload = result?.target_entity || {};
      const nextSelectedEntity = {
        ...target,
        id: String(targetPayload.id || targetId),
        name: String(targetPayload.name || target?.name || "").trim() || target?.name,
        type:
          String(targetPayload.entity_type || target?.type || target?.entity_type || "").trim() ||
          target?.type,
      };
      setMergeConfirm({ open: false, source: null, target: null });
      setSelectedEntity(nextSelectedEntity);
      setSelectedEntityFull(null);
      setActiveTab(TAB_KEYS.detail);
      setShowGraphPanel(false);
      setMergeNotice(
        `Marked “${source?.name || "item"}” as a duplicate of “${nextSelectedEntity.name || target?.name || "item"}”.`
      );
      window.setTimeout(() => setMergeNotice(""), 6000);
      await refreshEntities();
      await loadSelectedEntityFull(nextSelectedEntity, { bypassCache: true });
    } catch (e) {
      setMergeError(e?.message || "Failed to mark as duplicate.");
    } finally {
      setMergingEntity(false);
    }
  }, [loadSelectedEntityFull, mergeConfirm.source, mergeConfirm.target, refreshEntities]);

  const selectedEntityForMerge = selectedEntityFull || selectedEntity;
  const canMergeSelectedEntity = canShowExplorerMergeAction(
    selectedEntityForMerge,
    isSteward
  );
  const isDetailTab = activeTab === TAB_KEYS.detail;
  const showApprovalSelectionControls = canSeeOwnProposed && !isCreateTab && !isDetailTab;
  const canSubmitSelectedPending = isEntityPendingForApproval(selectedEntityForMerge);
  const detailTypeLabel = String(
    selectedEntityFull?.type ||
      selectedEntityFull?.entity_type ||
      selectedEntity?.type ||
      selectedEntity?.entity_type ||
      ""
  ).trim();
  const detailStatusLabel = formatEntityMapStatusLabel(
    selectedEntityFull || selectedEntity
  );

  const handleDetailBack = () => {
    if (entityNavBack) {
      runIfAllowedToLeaveEntityDetail(entityNavBack.onBack);
      return;
    }
    runIfAllowedToLeaveEntityDetail(() => {
      clearEntityNavStack();
      setShowGraphPanel(false);
      setActiveTab(TAB_KEYS.browse);
    });
  };

  // Mobile chrome: tabs stay visible except Graph canvas (persistent chrome policy).
  useEffect(() => {
    // Always restore tabs+header when leaving this explorer instance (back to Map).
    const restoreTabChrome = () => {
      setChrome({
        chromeMode: "tabs",
        actionTray: null,
        hideShellHeader: false,
      });
    };

    if (!isMobile) {
      setChrome({
        chromeMode: "tabs",
        actionTray: null,
        hideShellHeader: false,
      });
      return restoreTabChrome;
    }

    // Graph chrome is desktop-only (mobile never opens the panel).
    if (!isMobile && showGraphPanel && selectedEntity?.id) {
      setChrome({
        chromeMode: "immersive",
        contextTitle: selectedEntity?.name
          ? `Graph · ${selectedEntity.name}`
          : "Graph",
        contextSubtitle:
          entityNavStack.length > 0
            ? `${entityNavStack.length + 1} deep`
            : "",
        onBack: () =>
          runIfAllowedToLeaveEntityDetail(() => setShowGraphPanel(false)),
        backLabel: "Item",
        headerTrailing: null,
        actionTray: null,
        hideShellHeader: false,
      });
      return restoreTabChrome;
    }

    if (isDetailTab && selectedEntity?.id) {
      const title =
        selectedEntity?.name ||
        selectedEntityFull?.name ||
        (loadingSelected ? "Opening…" : "Entity");
      const backLabel = entityNavBack
        ? entityNavBack.label.replace(/^Back to\s+/i, "")
        : "Search";
      const onBack = () => handleDetailBack();

      setChrome({
        chromeMode: "tabs",
        contextTitle: title,
        contextSubtitle:
          entityNavStack.length > 0
            ? `${entityNavStack.length + 1} deep`
            : [detailTypeLabel, detailStatusLabel].filter(Boolean).join(" · "),
        onBack,
        backLabel,
        headerTrailing: (
          <button
            type="button"
            onClick={() => setMobileActionsOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-600 active:bg-slate-100"
            aria-label="Actions"
            title="Actions"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <circle cx="10" cy="4" r="1.5" />
              <circle cx="10" cy="10" r="1.5" />
              <circle cx="10" cy="16" r="1.5" />
            </svg>
          </button>
        ),
        actionTray: null,
        hideShellHeader: false,
      });
      return restoreTabChrome;
    }

    const browseTitle = isCreateTab ? "Add item" : "Browse map";
    setChrome({
      chromeMode: "tabs",
      contextTitle: browseTitle,
      contextSubtitle: "",
      onBack: onBackToGallery
        ? () => runIfAllowedToLeaveEntityDetail(onBackToGallery)
        : null,
      backLabel: onBackToGallery ? "Map" : "",
      headerTrailing: null,
      actionTray: null,
      hideShellHeader: false,
    });
    return restoreTabChrome;
  }, [
    isMobile,
    showGraphPanel,
    isDetailTab,
    isCreateTab,
    selectedEntity,
    selectedEntityFull,
    loadingSelected,
    entityNavBack,
    entityNavStack.length,
    detailTypeLabel,
    detailStatusLabel,
    onBackToGallery,
    setChrome,
  ]);

  const desktopGraphFill =
    Boolean(showGraphPanel) && !isMobile && isDetailTab && Boolean(selectedEntity);
  // Desktop Browse/detail: free-flow like Inbox (page grows; shell <main> scrolls).
  // Only Graph keeps a filled pane. Mobile keeps page-owned scroll.

  return (
    <section
      className={
        isMobile || desktopGraphFill
          ? "dp-no-x-overflow flex min-h-0 min-w-0 flex-1 flex-col overflow-y-hidden"
          : "dp-no-x-overflow min-w-0"
      }
    >
      <div
        className={v2cx(
          "dp-no-x-overflow min-w-0 max-w-full overscroll-y-contain",
          desktopGraphFill
            ? "flex min-h-0 flex-1 flex-col gap-3 overflow-y-hidden"
            : isMobile
              ? "dp-scroll-y-only min-h-0 flex-1 space-y-3"
              : "space-y-5"
        )}
      >
      {/* Hidden mobile Generate Document trigger for Actions sheet */}
      {isMobile &&
      isDetailTab &&
      selectedEntity?.id &&
      isApplicationEntity(selectedEntityFull || selectedEntity) ? (
        <div className="sr-only" aria-hidden>
          <GenerateDocumentButton
            entityId={selectedEntity.id}
            entityName={
              selectedEntityFull?.name || selectedEntity.name || ""
            }
            density="mobile"
            triggerId="dp-mobile-gen-doc-trigger"
          />
        </div>
      ) : null}

      {/* Desktop page toolbar. Hidden in Graph mode — the graph header has
          its own Back/title, and the graph needs the height. */}
      <div
        className={
          desktopGraphFill
            ? "hidden"
            : "hidden min-w-0 max-w-full rounded-xl border border-slate-200 bg-white px-2 py-1.5 sm:px-2.5 sm:py-2 md:block"
        }
      >
        <div className="flex min-w-0 max-w-full items-center justify-between gap-2 sm:gap-3">
          <div className="min-w-0 flex-1 overflow-hidden">
            <div className="flex min-w-0 items-center gap-1.5">
              {isDetailTab ? (
                <button
                  type="button"
                  onClick={handleDetailBack}
                  className="inline-flex shrink-0 items-center gap-0.5 text-sm font-medium text-blue-700 hover:text-blue-800"
                  aria-label={entityNavBack ? entityNavBack.label : "Back to search"}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-5 w-5 shrink-0"
                    aria-hidden
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>
                    {entityNavBack ? entityNavBack.label : "Back to search"}
                  </span>
                </button>
              ) : onBackToGallery ? (
                <button
                  type="button"
                  onClick={() => runIfAllowedToLeaveEntityDetail(onBackToGallery)}
                  className="inline-flex shrink-0 items-center gap-0.5 text-sm font-medium text-blue-700 hover:text-blue-800"
                  aria-label="Back to your map"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-5 w-5 shrink-0"
                    aria-hidden
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Back to your map</span>
                </button>
              ) : null}
              <h2 className="truncate text-base font-semibold tracking-tight sm:text-xl md:text-2xl">
                {isCreateTab
                  ? "Add item"
                  : isDetailTab
                    ? selectedEntity?.name ||
                      selectedEntityFull?.name ||
                      (loadingSelected ? "Opening…" : "Entity details")
                    : "Browse map"}
              </h2>
            </div>
            <p className={`${tokens.surface.mutedText} mt-0.5 hidden text-sm md:block`}>
              {isCreateTab
                ? "Name it, pick a type, and optionally add a description. Open Advanced details for the rest."
                : isDetailTab
                  ? loadingSelected && !selectedEntity?.name
                    ? "Loading entity details from your map…"
                    : [detailTypeLabel, detailStatusLabel].filter(Boolean).join(" · ") ||
                      "Review and edit this item on your map."
                  : canSeeOwnProposed
                    ? "Search and edit items on your map. Your pending drafts show an amber badge until approved."
                    : "Search and edit approved items on your map."}
            </p>
            {isDetailTab && (detailTypeLabel || detailStatusLabel) ? (
              <p className={`${tokens.surface.subtleText} mt-0.5 truncate text-xs md:hidden`}>
                {[detailTypeLabel, detailStatusLabel].filter(Boolean).join(" · ")}
              </p>
            ) : null}
          </div>

          <div className="flex min-w-0 max-w-full shrink-0 flex-wrap items-center justify-end gap-1.5 sm:gap-2">
          {isDetailTab && selectedEntity?.id ? (
            <>
              {!isMobile ? (
                <button
                  type="button"
                  onClick={() =>
                    runIfAllowedToLeaveEntityDetail(() =>
                      setShowGraphPanel((v) => !v)
                    )
                  }
                  className={`${tokens.button.secondary} inline-flex items-center gap-1.5 !px-2.5 sm:!px-3`}
                  aria-expanded={showGraphPanel}
                  aria-label={showGraphPanel ? "Hide graph" : "Show graph"}
                  title={showGraphPanel ? "Hide graph" : "Show graph"}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-4 w-4"
                    aria-hidden
                  >
                    <path d="M3 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm8 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V4zM3 12a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1v-4zm8 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                  </svg>
                  <span className="hidden sm:inline">
                    {showGraphPanel ? "Hide graph" : "Graph"}
                  </span>
                </button>
              ) : null}
              {canContribute &&
              (typeof onNavigate === "function" ||
                typeof onBackToGallery === "function") ? (
                <button
                  type="button"
                  onClick={() =>
                    runIfAllowedToLeaveEntityDetail(() =>
                      openAddKnowledgeForEntity(
                        selectedEntityFull || selectedEntity
                      )
                    )
                  }
                  className={`${tokens.button.secondary} inline-flex items-center gap-1.5 !px-2.5 sm:!px-3`}
                  title="Add knowledge"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  <span className="hidden sm:inline">Add knowledge</span>
                </button>
              ) : null}
              {isApplicationEntity(selectedEntityForMerge) ? (
                <GenerateDocumentButton
                  entityId={selectedEntity.id}
                  entityName={
                    selectedEntityFull?.name || selectedEntity.name || ""
                  }
                  density="desktop"
                />
              ) : null}
              <V2OverflowMenu label="Actions">
                {canMergeSelectedEntity ? (
                  <V2OverflowMenuItem
                    onClick={() => handleRequestMergeEntity(selectedEntityForMerge)}
                  >
                    Mark as duplicate of…
                  </V2OverflowMenuItem>
                ) : null}
                {canSubmitSelectedPending ? (
                  <V2OverflowMenuItem
                    onClick={() => {
                      setSelectedIds(new Set([String(selectedEntity.id)]));
                      setSubmitModalOpen(true);
                    }}
                  >
                    Submit for approval
                  </V2OverflowMenuItem>
                ) : null}
                <V2OverflowMenuSeparator />
                {canContribute ? (
                  <V2OverflowMenuItem
                    onClick={() =>
                      runIfAllowedToLeaveEntityDetail(() => {
                        setSelectedEntity(null);
                        setSelectedEntityFull(null);
                        setCreateFormKey((k) => k + 1);
                        setActiveTab(TAB_KEYS.create);
                      })
                    }
                  >
                    Add item
                  </V2OverflowMenuItem>
                ) : null}
                <V2OverflowMenuItem
                  disabled={refreshing}
                  onClick={() => {
                    void refreshEntities();
                    void loadSelectedEntityFull(selectedEntity, { bypassCache: true });
                  }}
                >
                  {refreshing ? "Refreshing…" : "Refresh"}
                </V2OverflowMenuItem>
                {onBackToGallery ? (
                  <V2OverflowMenuItem
                    onClick={() => runIfAllowedToLeaveEntityDetail(onBackToGallery)}
                  >
                    Back to your map
                  </V2OverflowMenuItem>
                ) : null}
                <V2OverflowMenuSeparator />
                <V2OverflowMenuItem
                  danger
                  onClick={() => handleRequestDeleteEntity(selectedEntityForMerge)}
                >
                  Delete
                </V2OverflowMenuItem>
              </V2OverflowMenu>
            </>
          ) : (
            <>
          {!isCreateTab && showApprovalSelectionControls ? (
            <>
              {selectionMode ? (
                <>
                  <button
                    type="button"
                    disabled={selectedIds.size === 0}
                    onClick={() => setSubmitModalOpen(true)}
                    className={`${tokens.button.primary} disabled:opacity-50 whitespace-nowrap !px-2.5 sm:!px-3 text-xs sm:text-sm`}
                  >
                    <span className="sm:hidden">Submit ({selectedIds.size})</span>
                    <span className="hidden sm:inline">
                      Submit for approval ({selectedIds.size})
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className={`${tokens.button.secondary} whitespace-nowrap !px-2.5 sm:!px-3 text-xs sm:text-sm`}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setApprovalFilter("proposed");
                    setPage(1);
                    setSelectionMode(true);
                    setSelectedIds(new Set());
                  }}
                  className={`${tokens.button.secondary} whitespace-nowrap !px-2.5 sm:!px-3 text-xs sm:text-sm`}
                >
                  <span className="sm:hidden">Select</span>
                  <span className="hidden sm:inline">Select for approval</span>
                </button>
              )}
            </>
          ) : null}
          {!isCreateTab && canContribute ? (
            <button
              type="button"
              onClick={() =>
                runIfAllowedToLeaveEntityDetail(() => {
                  setSelectedEntity(null);
                  setSelectedEntityFull(null);
                  setCreateFormKey((k) => k + 1);
                  setActiveTab(TAB_KEYS.create);
                })
              }
              className={`${tokens.button.success} whitespace-nowrap !px-2.5 sm:!px-3 text-xs sm:text-sm`}
            >
              <span className="sm:hidden">Add</span>
              <span className="hidden sm:inline">Add item</span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              void refreshEntities();
              if (selectedEntity?.id) {
                void loadSelectedEntityFull(selectedEntity, { bypassCache: true });
              }
            }}
            disabled={refreshing}
            className={`${tokens.button.primary} disabled:opacity-50 inline-flex items-center gap-1.5 !px-2.5 sm:!px-3 text-xs sm:text-sm`}
            title={refreshing ? "Refreshing…" : "Refresh"}
            aria-label={refreshing ? "Refreshing" : "Refresh"}
          >
            <svg
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="hidden sm:inline">
              {refreshing ? "Refreshing…" : "Refresh"}
            </span>
          </button>
            </>
          )}
          </div>
        </div>
      </div>

      {submitNotice ? (
        <div className="flex shrink-0 flex-wrap items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-950">
          <span className="min-w-0 flex-1">{submitNotice}</span>
          {typeof onNavigate === "function" ? (
            <button
              type="button"
              onClick={() => onNavigate(V2_ROUTES.baselineApprovals)}
              className="text-xs font-medium underline"
            >
              Open Approvals
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setSubmitNotice("")}
            className="text-xs font-medium underline"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {deletingEntity || deleteNotice ? (
        <div
          className={v2cx(
            "flex shrink-0 flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm",
            deletingEntity
              ? "border-amber-200 bg-amber-50 text-amber-950"
              : "border-emerald-200 bg-emerald-50 text-emerald-900"
          )}
          role="status"
          aria-live="polite"
          aria-busy={deletingEntity}
        >
          {deletingEntity ? (
            <>
              <svg
                className="h-5 w-5 shrink-0 animate-spin text-amber-700"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="font-medium">
                Deleting “{deletingEntityLabel || "item"}”…
              </span>
            </>
          ) : (
            <>
              <span className="text-emerald-700" aria-hidden>
                ✓
              </span>
              <span className="font-medium flex-1 min-w-0">{deleteNotice}</span>
              <button
                type="button"
                onClick={dismissDeleteNotice}
                className="text-xs text-emerald-800 hover:text-emerald-950 underline"
              >
                Dismiss
              </button>
            </>
          )}
        </div>
      ) : null}

      {mergingEntity || mergeNotice ? (
        <div
          className={v2cx(
            "rounded-xl border px-4 py-3 flex flex-wrap items-center gap-3 text-sm",
            mergingEntity
              ? "border-amber-200 bg-amber-50 text-amber-950"
              : "border-emerald-200 bg-emerald-50 text-emerald-900"
          )}
          role="status"
          aria-live="polite"
          aria-busy={mergingEntity}
        >
          {mergingEntity ? (
            <>
              <svg
                className="h-5 w-5 shrink-0 animate-spin text-amber-700"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="font-medium">Merging items…</span>
            </>
          ) : (
            <>
              <span className="text-emerald-700" aria-hidden>
                ✓
              </span>
              <span className="font-medium flex-1 min-w-0">{mergeNotice}</span>
              <button
                type="button"
                onClick={dismissMergeNotice}
                className="text-xs text-emerald-800 hover:text-emerald-950 underline"
              >
                Dismiss
              </button>
            </>
          )}
        </div>
      ) : null}

      <div
        className={v2cx(
          "dp-no-x-overflow min-w-0 max-w-full",
          desktopGraphFill && "flex min-h-0 flex-1 flex-col",
          isMobile
            ? "border-y border-slate-200 bg-white"
            : // No shadow: box-shadow can inflate scrollWidth and spawn an H bar.
              "rounded-xl border border-slate-200 bg-white"
        )}
      >
        {activeTab === TAB_KEYS.browse ? (
          <div
            className={
              isMobile ? "space-y-3 pt-3" : "dp-no-x-overflow space-y-4 p-4"
            }
          >
            <div
              className={v2cx(
                "grid min-w-0 gap-3 md:grid-cols-4",
                isMobile && "px-3"
              )}
            >
              <div className="flex min-w-0 gap-2 overflow-hidden md:col-span-2">
                <input
                  ref={searchInputRef}
                  type="search"
                  value={searchInput}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitSearch();
                    }
                  }}
                  placeholder="Search your map by name or alias..."
                  className="min-w-0 flex-1 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900"
                />
                <button
                  type="button"
                  onClick={submitSearch}
                  className={`${tokens.button.primary} shrink-0`}
                >
                  Search
                </button>
              </div>
              <div className="min-w-0 overflow-hidden">
                <select
                  value={entityTypeFilter}
                  onChange={(e) => {
                    setEntityTypeFilter(e.target.value);
                    setPage(1);
                  }}
                  className="box-border w-full max-w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900"
                  style={{ minWidth: 0, width: "100%" }}
                >
                  <option value="all">All types</option>
                  {entityTypeOptions.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              {canSeeOwnProposed ? (
                <div className="min-w-0 overflow-hidden">
                  <select
                    value={approvalFilter}
                    onChange={(e) => {
                      setApprovalFilter(e.target.value === "proposed" ? "proposed" : "all");
                      setPage(1);
                      clearSelection();
                    }}
                    className="box-border w-full max-w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900"
                    style={{ minWidth: 0, width: "100%" }}
                  >
                    <option value="all">All statuses</option>
                    <option value="proposed">My pending only</option>
                  </select>
                </div>
              ) : null}
            </div>

            {selectionMode ? (
              <div
                className={v2cx(
                  "flex shrink-0 flex-wrap items-center justify-between gap-2 text-xs text-indigo-800 bg-indigo-50 border border-indigo-100 px-3 py-2",
                  isMobile ? "mx-3 rounded-lg" : "rounded-lg"
                )}
              >
                <span>Select pending items to send for approval. Only pending rows can be submitted.</span>
                {proposedOnPage.length > 0 ? (
                  <button
                    type="button"
                    onClick={selectAllPendingOnPage}
                    className="font-medium underline whitespace-nowrap"
                  >
                    Select all on this page ({proposedOnPage.length})
                  </button>
                ) : null}
              </div>
            ) : null}

            <div
              className={v2cx(
                "min-w-0",
                isMobile
                  ? "overflow-hidden border-t border-slate-200"
                  : "overflow-hidden rounded-lg border border-slate-200"
              )}
            >
              {loading && entities.length === 0 ? (
                <p className="px-3 py-6 text-sm text-slate-500 md:px-4">Loading items…</p>
              ) : entities.length === 0 ? (
                <p className="px-3 py-6 text-sm text-slate-500 md:px-4">No items found.</p>
              ) : (
                <ul className="divide-y divide-slate-200">
                  {entities.map((entity) => {
                    const isSelected = String(selectedEntity?.id || "") === String(entity?.id || "");
                    const approvalRaw = String(
                      entity?.approval_status || entity?.approvalStatus || ""
                    ).trim();
                    const isProposed = approvalRaw.toLowerCase() === "proposed";
                    const awaitingApproval = Boolean(entity?.awaiting_baseline_approval);
                    const checked = selectedIds.has(String(entity.id));
                    const canSelect = selectionMode && isProposed && !awaitingApproval;
                    const matchingAliases = aliasesMatchingSearch(
                      entity?.alias_names,
                      searchTerm
                    );
                    const nameMatchesSearch = String(entity?.name || "")
                      .toLowerCase()
                      .includes(String(searchTerm || "").trim().toLowerCase());
                    return (
                      <li
                        key={entity.id}
                        className={isMobile ? "px-3 py-3" : "px-4 py-3"}
                      >
                        <div className="flex items-start gap-3">
                          {canSelect ? (
                            <input
                              type="checkbox"
                              className="mt-1 h-4 w-4 rounded border-slate-300"
                              checked={checked}
                              onChange={() => toggleSelected(entity.id)}
                              aria-label={`Select ${entity?.name || "item"}`}
                            />
                          ) : null}
                          <button
                            type="button"
                            onClick={() => {
                              if (canSelect) {
                                toggleSelected(entity.id);
                                return;
                              }
                              runIfAllowedToLeaveEntityDetail(() => {
                                setEntityNavStack([]);
                                setSelectedEntity(entity);
                                setActiveTab(TAB_KEYS.detail);
                              });
                            }}
                            className={v2cx(
                              "text-left flex-1 min-w-0",
                              isSelected ? "text-blue-700" : "text-slate-900"
                            )}
                          >
                          <div className="flex items-center gap-2 min-w-0">
                            <p className="text-sm font-medium truncate flex-1 min-w-0">
                              {entity?.name || "Unnamed item"}
                            </p>
                            {isProposed ? (
                              awaitingApproval ? (
                                <span className="shrink-0 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded border border-sky-300 bg-sky-50 text-sky-900">
                                  Awaiting approval
                                </span>
                              ) : (
                                <span className="shrink-0 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-900">
                                  Pending
                                </span>
                              )
                            ) : approvalRaw.toLowerCase() === "approved" ? (
                              <span className="shrink-0 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-800">
                                On map
                              </span>
                            ) : null}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {String(entity?.type || entity?.entity_type || "unknown")}
                          </p>
                          {!nameMatchesSearch && matchingAliases.length > 0 ? (
                            <p className="text-xs text-slate-500 mt-0.5">
                              Also known as {matchingAliases.join(", ")}
                            </p>
                          ) : null}
                          <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                            {entity?.description || "No description"}
                          </p>
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div
              className={v2cx(
                "flex shrink-0 items-center justify-between gap-3",
                isMobile ? "px-3 pb-3" : null
              )}
            >
              <p className="text-xs text-slate-500">
                Showing {entities.length} of {total.toLocaleString()} items
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  className={`${tokens.button.secondary} disabled:opacity-50`}
                >
                  Prev
                </button>
                <span className="text-xs text-slate-600">
                  Page {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  className={`${tokens.button.secondary} disabled:opacity-50`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {activeTab === TAB_KEYS.detail ? (
          <div
            ref={detailAnchorRef}
            className={v2cx(
              "v2-explorer-light scroll-mt-28",
              showGraphPanel && !isMobile && selectedEntity && !isCreateTab
                ? "flex min-h-0 flex-1 flex-col overflow-hidden px-2 py-2 md:px-3 md:py-2"
                : isMobile
                  ? "min-h-[320px] px-2.5 py-1.5 space-y-2.5"
                  : "space-y-4 px-4 py-4 md:px-5 md:py-5"
            )}
          >
            {loadingSelected &&
            selectedEntity &&
            !isCreateTab &&
            !(showGraphPanel && !isMobile) ? (
              <div
                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700"
                role="status"
                aria-live="polite"
                aria-busy="true"
              >
                <svg
                  className="h-4 w-4 shrink-0 animate-spin text-slate-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Loading connections and details…</span>
              </div>
            ) : null}
            {selectedEntity && !isCreateTab ? (
              showGraphPanel && !isMobile ? (
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                    <GraphVisualization
                      embedded
                      embeddedFillHeight
                      embeddedUnifiedChrome
                      headerLeading={
                        <>
                          <button
                            type="button"
                            onClick={() => setShowGraphPanel(false)}
                            className={`${tokens.button.secondary} inline-flex shrink-0 items-center gap-1.5 text-sm`}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              className="h-4 w-4"
                              aria-hidden
                            >
                              <path
                                fillRule="evenodd"
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Back to item
                          </button>
                          <span className="min-w-0 truncate text-sm font-medium text-slate-800">
                            {selectedEntity?.name
                              ? `Connections — ${selectedEntity.name}`
                              : "Connections"}
                          </span>
                        </>
                      }
                      onClose={null}
                      initialNodes={explorerGraphBootstrap.initialNodes}
                      initialEdges={explorerGraphBootstrap.initialEdges}
                      centerEntityId={selectedEntity.id}
                      autoLoadCenter={true}
                      suppressSelection={false}
                    />
                  </div>
                </div>
              ) : (
                <EntityReadOnlyView
                  embedded
                  hideHeader
                  architectureExplorerView
                  disableLiveRefresh
                  canEdit={canContribute}
                  onRequestDelete={
                    canContribute ? handleRequestDeleteEntity : undefined
                  }
                  entity={selectedEntityFull || selectedEntity}
                  relationships={
                    Array.isArray(selectedEntityFull?.relationships)
                      ? selectedEntityFull.relationships
                      : Array.isArray(selectedEntity?.relationships)
                        ? selectedEntity.relationships
                        : []
                  }
                  relationshipsLoading={
                    loadingSelected ||
                    String(selectedEntityFull?.id || "") !==
                      String(selectedEntity?.id || "")
                  }
                  allEntities={allEntitiesMap}
                  onClose={() => {
                    clearEntityNavStack();
                    setActiveTab(TAB_KEYS.browse);
                  }}
                  navigationBack={entityNavBack}
                  onNavigate={onNavigate}
                  onNavigateToRelatedEntity={handleNavigateToRelatedEntity}
                  onUnsavedEntityDetailChangesChange={setUnsavedEntityDetailSummary}
                  requestLeaveWithUnsavedChanges={requestLeaveEntityDetail}
                  onRelationshipRemoved={(relationshipId) => {
                    setSelectedEntityFull((prev) => {
                      if (!prev?.relationships) return prev;
                      const next = prev.relationships.filter(
                        (r) => r?.id !== relationshipId
                      );
                      return { ...prev, relationships: next };
                    });
                  }}
                  onRelationshipsPersisted={(meta) => {
                    if (meta?.entityDeleted) {
                      setSelectedEntityFull(null);
                      setSelectedEntity(null);
                      setActiveTab(TAB_KEYS.browse);
                      return;
                    }
                    if (selectedEntity?.id) {
                      void loadSelectedEntityFull(selectedEntity, {
                        bypassCache: true,
                      });
                    }
                  }}
                  onEntityPersisted={() => {
                    if (selectedEntity?.id) {
                      void loadSelectedEntityFull(selectedEntity, {
                        bypassCache: true,
                      });
                    }
                  }}
                />
              )
            ) : (
              <p className="py-6 text-sm text-slate-500">
                Select an item from the list to view details.
              </p>
            )}
          </div>
        ) : null}

        {isCreateTab ? (
          <div className="v2-explorer-light p-4">
            <EntityForm
              key={`create-${createDefaultEntityType}-${createFormKey}`}
              embedded
              editingEntity={null}
              defaultEntityType={createDefaultEntityType}
              onSave={handleEntitySaved}
              onCancel={() => {
                setActiveTab(TAB_KEYS.browse);
              }}
              entities={entities}
              allEntities={allEntitiesMap}
              relationships={[]}
              readOnly={false}
            />
          </div>
        ) : null}
      </div>

      {loadingSelected && activeTab !== TAB_KEYS.detail && activeTab !== TAB_KEYS.create ? (
        <p className="text-xs text-slate-500">Refreshing item details…</p>
      ) : null}
      {error ? (
        <p className="text-sm text-rose-600">{error}</p>
      ) : null}
      </div>

      <MobileBottomSheet
        open={mobileActionsOpen}
        onClose={() => setMobileActionsOpen(false)}
        title="Actions"
        height="half"
      >
        <ul className="space-y-1">
          {canContribute &&
          (typeof onNavigate === "function" ||
            typeof onBackToGallery === "function") ? (
            <li>
              <button
                type="button"
                className="flex w-full items-center rounded-xl px-3 py-3.5 text-left text-sm font-semibold text-emerald-800 active:bg-emerald-50"
                onClick={() => {
                  setMobileActionsOpen(false);
                  runIfAllowedToLeaveEntityDetail(() =>
                    openAddKnowledgeForEntity(
                      selectedEntityFull || selectedEntity
                    )
                  );
                }}
              >
                Add knowledge
              </button>
            </li>
          ) : null}
          {isApplicationEntity(selectedEntityFull || selectedEntity) ? (
            <li>
              <button
                type="button"
                className="flex w-full items-center rounded-xl px-3 py-3.5 text-left text-sm font-medium text-slate-800 active:bg-slate-100"
                onClick={() => {
                  setMobileActionsOpen(false);
                  document
                    .getElementById("dp-mobile-gen-doc-trigger")
                    ?.click();
                }}
              >
                Generate document
              </button>
            </li>
          ) : null}
          {canSubmitSelectedPending ? (
            <li>
              <button
                type="button"
                className="flex w-full items-center rounded-xl px-3 py-3.5 text-left text-sm font-medium text-slate-800 active:bg-slate-100"
                onClick={() => {
                  setMobileActionsOpen(false);
                  setSelectedIds(new Set([String(selectedEntity.id)]));
                  setSubmitModalOpen(true);
                }}
              >
                Submit for approval
              </button>
            </li>
          ) : null}
          <li>
            <button
              type="button"
              className="flex w-full items-center rounded-xl px-3 py-3.5 text-left text-sm font-medium text-rose-700 active:bg-rose-50"
              onClick={() => {
                setMobileActionsOpen(false);
                handleRequestDeleteEntity(selectedEntityForMerge);
              }}
            >
              Delete
            </button>
          </li>
        </ul>
      </MobileBottomSheet>

      {deleteConfirm.open ? (
        <div
          className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-[70] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deletingEntity) cancelDeleteEntity();
          }}
        >
          <div
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="v2-delete-entity-title"
            aria-describedby="v2-delete-entity-desc"
          >
            <h3 id="v2-delete-entity-title" className="text-lg font-semibold text-slate-900">
              Delete item
            </h3>
            <p id="v2-delete-entity-desc" className="mt-2 text-sm text-slate-600">
              Are you sure you want to delete{" "}
              <span className="font-medium text-slate-900">{deleteConfirm.entityName}</span>?
              This permanently removes it from your map and its connections.
            </p>
            {deletingEntity ? (
              <div
                className="mt-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950"
                role="status"
                aria-live="polite"
              >
                <svg
                  className="h-4 w-4 shrink-0 animate-spin text-amber-700"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Deleting…</span>
              </div>
            ) : null}
            {deleteError ? (
              <p className="mt-3 text-sm text-rose-700" role="alert">
                {deleteError}
              </p>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelDeleteEntity}
                disabled={deletingEntity}
                className={`${tokens.button.secondary} disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmDeleteEntity()}
                disabled={deletingEntity}
                className={`${tokens.button.danger} disabled:opacity-50 inline-flex items-center gap-2`}
              >
                {deletingEntity ? (
                  <>
                    <svg
                      className="h-4 w-4 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Deleting…
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <EnrichmentEntityPickerModal
        key={`merge-picker-${mergePickerState?.source?.id || "none"}-${getExplorerMergeEntityType(mergePickerState?.source) || "unknown"}`}
        isOpen={mergePickerState.open}
        isInlineLight
        title={
          mergePickerState?.source?.name
            ? `Mark “${mergePickerState.source.name}” as a duplicate of…`
            : "Mark as duplicate of…"
        }
        description={
          mergePickerState?.source?.name
            ? `Only matching ${mergePickerState.source.type || "types"} are shown. Pick the item that should remain on your map; this one will be merged into it.`
            : ""
        }
        confirmLabel="Continue"
        entityType={getExplorerMergeEntityType(mergePickerState?.source)}
        priorityEntity={mergePickerState?.source}
        excludeEntityIds={
          mergePickerState?.source?.id ? [mergePickerState.source.id] : []
        }
        onClose={closeMergePicker}
        onSelect={handleSelectMergeTarget}
      />

      {mergeConfirm.open ? (
        <div
          className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-[80] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !mergingEntity) closeMergeConfirm();
          }}
        >
          <div
            className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="v2-merge-entity-title"
            aria-describedby="v2-merge-entity-desc"
          >
            <h3 id="v2-merge-entity-title" className="text-lg font-semibold text-slate-900">
              Confirm duplicate
            </h3>
            <p id="v2-merge-entity-desc" className="mt-2 text-sm text-slate-600">
              Mark{" "}
              <span className="font-medium text-slate-900">
                {mergeConfirm.source?.name || "this item"}
              </span>{" "}
              as a duplicate of{" "}
              <span className="font-medium text-slate-900">
                {mergeConfirm.target?.name || "the selected item"}
              </span>
              . The duplicate will be removed from your map; its connections and
              aliases move over where possible.
            </p>
            {mergeError ? (
              <p className="mt-3 text-sm text-rose-700" role="alert">
                {mergeError}
              </p>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeMergeConfirm}
                disabled={mergingEntity}
                className={`${tokens.button.secondary} disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmMergeEntities()}
                disabled={
                  mergingEntity ||
                  String(mergeConfirm.source?.id || "") ===
                    String(mergeConfirm.target?.id || "")
                }
                className={`${tokens.button.danger} disabled:opacity-50 inline-flex items-center gap-2`}
              >
                {mergingEntity ? (
                  <>
                    <svg
                      className="h-4 w-4 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Confirming…
                  </>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <UnsavedEntityDetailChangesDialog
        open={unsavedEntityDetailDialogOpen}
        summary={unsavedEntityDetailSummary}
        onStay={stayOnEntityDetailPage}
        onLeaveWithoutSaving={leaveEntityDetailWithoutSaving}
      />

      <SubmitForBaselineApprovalModal
        open={submitModalOpen}
        entityIds={[...selectedIds]}
        onClose={() => setSubmitModalOpen(false)}
        onSubmitted={(result) => {
          const created = Number(result?.created || 0);
          setSubmitNotice(
            `Submitted ${created} item${created === 1 ? "" : "s"} for approval.`
          );
          clearSelection();
          setSelectionMode(false);
          void refreshOwnProposedCount();
          void refreshEntities();
        }}
      />
    </section>
  );
};

export default V2ExplorerPage;
